// Auftrags-Detail-Modal mit lückenloser Historie.
// Zeigt alles, was zu einem Auftrag gehört: Quelle (Shop-Bestellung,
// Anfrage), E-Mail-Thread, verknüpfte Aufgaben, Dokumente,
// Produktbegleitdokumente und Lager-Buchungen.
// Admin/Innendienst können Verknüpfungen entfernen (mit Bestätigung).
import { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../lib/auth-fetch';
import { usePermissions } from '../hooks/usePermissions';
import OrderTasksChecklistPanel from '../pages/dashboard/OrderTasksChecklistPanel';
import { TaskCard, TaskDialog } from '../pages/dashboard/TasksPage';
import { useAuth } from '../contexts/AuthContext';
import {
  X, ShoppingBag, Mail, ClipboardList, FileText, FileCheck2, Boxes,
  Plus, Trash2, ExternalLink, AlertTriangle, CheckCircle2, RefreshCw, Cpu,
  Download, FileDown, ChevronDown, Sparkles, Pencil, StickyNote, Camera,
  Link as LinkIcon, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import OrderDocumentEditor from '../pages/dashboard/OrderDocumentEditor';

const fmtEur = (v, currency = 'EUR') =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency, minimumFractionDigits: 2 }).format(Number(v || 0));

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
  : '–';

const TABS = [
  { key: 'overview', label: 'Übersicht', icon: ClipboardList },
  { key: 'mails', label: 'E-Mails', icon: Mail },
  { key: 'tasks', label: 'Aufgaben', icon: ClipboardList },
  { key: 'documents', label: 'Dokumente', icon: FileText },
  { key: 'productDocs', label: 'Begleitdokument', icon: FileCheck2 },
  { key: 'components', label: 'Komponenten', icon: Cpu },
  { key: 'inventory', label: 'Lager', icon: Boxes },
  { key: 'note', label: 'Interne Notiz', icon: StickyNote },
];

// Welche Tabs sind bei Bestellungen (orderKind='bestellung') überhaupt
// sichtbar? Punkt 3 Runde 2 (28.02.2026): kein Begleitdokument,
// keine E-Mails — Bestellungen sind Lieferschein-basiert.
// 01.09.2026 (User-Wunsch): Komponenten-Tab bleibt sichtbar (Lager
// füllt dort Seriennummern nach), nur Begleitdokument + Mails weg.
const BESTELLUNG_TABS = new Set(['overview', 'tasks', 'documents', 'components', 'inventory', 'note']);

// Welche Tabs braucht welche Action? (Matrix-gesteuert 25.08.2026)
// Wenn `null`, ist der Tab für alle mit `orders.read` sichtbar.
const TAB_ACTION = {
  overview: 'orders.view_overview',
  mails: 'orders.view_mails',
  tasks: null,
  documents: null,
  productDocs: null,
  components: null,
  inventory: null,
  note: 'orders.view_internal_note',
};

// Utility: prüft, ob der User eine Action hat.
// Admin bekommt automatisch alle Rechte.
const hasAct = (user, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Array.isArray(user.actions) && user.actions.includes(action);
};

export default function OrderHistoryModal({ orderId, onClose, onReimported, initialTab }) {
  const { user } = useAuth();
  const role = user?.role;
  // Produktion sieht seit 25.08.2026 auch die Übersicht (mit maskierten
  // Preisen/Sync-Aktionen). E-Mails und Interne Notiz bleiben versteckt.
  const isProduktion = role === 'produktion';
  const canSyncEasybill = ['admin', 'innendienst', 'vertrieb', 'support'].includes(role);
  // Deep-Link (28.02.2026): Wenn `initialTab` gesetzt ist und den Key eines
  // bekannten Tabs trifft, öffnen wir das Modal direkt auf diesem Tab.
  const validTabKeys = TABS.map(t => t.key);
  const [tab, setTab] = useState(
    initialTab && validTabKeys.includes(initialTab) ? initialTab : 'overview'
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [users, setUsers] = useState([]);
  const [showSync, setShowSync] = useState(false);

  const load = useCallback(async () => {
    // 01.09.2026 (Bug-Fix): Nur beim ERSTEN Laden Skeleton anzeigen. Wenn
    // bereits Daten vorhanden sind, lädt der Endpoint still nach — sonst
    // unmountet React die Tabs (inkl. Kind-Modals wie
    // TechnikChecklistModal) bei jedem Speicher-Reload.
    setData(prev => {
      if (!prev) setLoading(true);
      return prev;
    });
    const r = await authFetch(`/api/orders/${orderId}/history`);
    if (r.ok) setData(await r.json());
    else toast.error('Auftragshistorie konnte nicht geladen werden');
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    load();
    authFetch('/api/auth/users').then(r => r.ok && r.json().then(setUsers));
    // Bugfix 28.02.2026: Kanban-Bell soll verschwinden, sobald der User
    // die Auftragshistorie öffnet — ohne Speichern oder Status-Wechsel.
    // Setzt `orders.activityReadBy[<email>]` auf now via bestehendem
    // Endpoint (`OrderTasksChecklistPanel` verwendete denselben Call).
    authFetch(`/api/integrations/easybill/orders/${orderId}/activity/read`, { method: 'POST' });
  }, [load, orderId]);

  const removeLink = async (kind, itemId, confirmText) => {
    if (!window.confirm(confirmText || 'Diese Verknüpfung wirklich aus der Auftragshistorie entfernen?')) return;
    const r = await authFetch(`/api/orders/${orderId}/history/${kind}/${itemId}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Entfernt'); await load(); }
    else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Entfernen fehlgeschlagen');
    }
  };

  const syncToEasybill = async (extraPositions) => {
    if (!data?.order?.easybillDocumentId
        && !window.confirm('Auftrag jetzt in Easybill anlegen?')) return;
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/sync-to-easybill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        docType: 'CHARGE',  // Easybill "Auftrag" — daraus kann Easybill Rechnung & Lieferschein erzeugen
        extraPositions: extraPositions || [],
      }),
    });
    if (r.ok) {
      const res = await r.json();
      toast.success(`In Easybill angelegt: ${res.easybillDocumentNumber}`);
      setShowSync(false);
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Easybill-Sync fehlgeschlagen');
    }
  };

  const reimport = async () => {
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/reimport-from-easybill`, { method: 'POST' });
    if (r.ok) {
      toast.success('Aus Easybill aktualisiert');
      await load();
      // Parent (Aufträge-Kanban) bekommt Bescheid, damit Auftragsnummer dort
      // ohne manuellen Reload sichtbar wird.
      if (typeof onReimported === 'function') {
        try { onReimported(); } catch { /* still */ }
      }
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Re-Import fehlgeschlagen');
    }
  };

  const finalize = async () => {
    // 01.09.2026: Bestellungen buchen Lager direkt aus `lineItems` — kein
    // Produktbegleitdokument. Aufträge nutzen weiterhin die BOM-Finalisierung.
    const isBestellungOrder = data?.order?.orderKind === 'bestellung';
    if (!window.confirm(
      isBestellungOrder
        ? 'Lager-Abzug jetzt durchführen?\n\n'
          + 'Alle bestellten Artikel werden aus dem Reiter „Lager" abgebucht. '
          + 'Diese Aktion kann nicht wiederholt werden.'
        : 'Lager-Abzug jetzt durchführen?\n\n'
          + 'Die Komponenten aus dem Produktbegleitdokument werden vom Lager abgebucht. '
          + 'Diese Aktion kann nicht wiederholt werden.',
    )) return;
    setFinalizing(true);
    const url = isBestellungOrder
      ? `/api/shop/orders/${orderId}/book-inventory-all`
      : `/api/order-documents/by-order/${orderId}/finalize`;
    const r = await authFetch(url, { method: 'POST' });
    setFinalizing(false);
    if (r.ok) {
      const res = await r.json();
      const n = (res.deducted?.length ?? res.booked?.length ?? 0);
      toast.success(`Lager gebucht: ${n} Positionen`);
      if (res.warnings && res.warnings.length > 0) {
        toast.warning(res.warnings.join(' · '));
      }
      if (res.autoCompleted) {
        toast.success('Auftrag automatisch als erledigt markiert — alle Punkte abgehakt.');
      }
      await load();
      onReimported?.();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Finalisierung fehlgeschlagen');
    }
  };

  if (!data && !loading) return null;
  const counts = data ? {
    mails: data.mails.length, tasks: data.tasks.length,
    documents: data.documents.length, productDocs: data.productDocuments.length,
    inventory: data.inventoryTransactions.length,
  } : {};

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="order-history-modal">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {/* 01.09.2026 (Bug-Fix): Bei Bestellungen heißt der Header
                „BESTELLUNG" statt „AUFTRAG" — analog zur Kanban-Karte. */}
            <div className="text-[10px] uppercase tracking-wide text-gray-400 font-mono">
              {data?.order?.orderKind === 'bestellung' ? 'Bestellung' : 'Auftrag'} · {data?.order?.id?.slice(0, 8)}
            </div>
            <h2 className="text-base font-semibold text-[#32373c] truncate">
              {data?.order?.easybillDocumentNumber || data?.order?.title || 'Auftrag'}
            </h2>
            <div className="text-xs text-gray-500 mt-0.5">
              {data?.order?.customerCompany || data?.order?.customerName}
              {data?.order?.grandTotalGross != null && ` · ${fmtEur(data.order.grandTotalGross, data.order.currency)}`}
              {data?.order?.status && ` · Status: ${data.order.status}`}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-400" data-testid="btn-close-history">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap border-b border-gray-100 px-2 bg-gray-50/50">
          {TABS.filter(t => {
            const req = TAB_ACTION[t.key];
            // Runde 2 (28.02.2026): Bestellungen zeigen nur ausgewählte Tabs.
            if (data?.order?.orderKind === 'bestellung' && !BESTELLUNG_TABS.has(t.key)) {
              return false;
            }
            return req == null || hasAct(user, req);
          }).map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            const c = counts[t.key];
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px ${
                  active ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
                {typeof c === 'number' && c > 0 && (
                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${active ? 'bg-[#9ec043]/20' : 'bg-gray-200 text-gray-500'}`}>{c}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-sm text-gray-400">Lade Historie …</div>
          ) : !data ? null : (
            <>
              {tab === 'overview' && <OverviewTab data={data} setData={setData} orderId={orderId} isProduktion={isProduktion} showPrices={hasAct(user, 'orders.view_prices')} onFinalize={finalize} finalizing={finalizing} onSync={syncToEasybill} showSync={showSync} setShowSync={setShowSync} onReimport={reimport} onChecklistChanged={async () => { await load(); onReimported?.(); }} />}
              {tab === 'mails' && <MailsTab mails={data.mails} onRemove={(id) => removeLink('mail', id, 'Mail-Verknüpfung wirklich entfernen?')} />}
              {tab === 'tasks' && <TasksTab orderId={orderId} order={data.order} initialTasks={data.tasks} onReload={load} users={users} currentUser={user} />}
              {tab === 'documents' && <DocumentsTab orderId={orderId} onRemove={(id) => removeLink('document', id, 'Verknüpfung zum Dokument entfernen?')} />}
              {tab === 'productDocs' && <ProductDocsTab
                docs={data.productDocuments}
                orderId={orderId}
                role={role}
                onRemove={(id) => removeLink('productDocument', id, 'Produktbegleitdokument wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')}
                onChanged={load}
              />}
              {tab === 'components' && data?.order?.orderKind === 'bestellung' && (
                <BestellungComponentsTab order={data.order} />
              )}
              {tab === 'components' && data?.order?.orderKind !== 'bestellung' && <ComponentsTab orderId={orderId} />}
              {tab === 'inventory' && data?.order?.orderKind === 'bestellung' && (
                <BestellungInventoryTab
                  order={data.order}
                  onBooked={async () => { await load(); onReimported?.(); }}
                />
              )}
              {tab === 'inventory' && data?.order?.orderKind !== 'bestellung' && <InventoryTab tx={data.inventoryTransactions} />}
              {tab === 'note' && <InternalNoteTab orderId={orderId} order={data.order} role={role} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ExtraPositionRow({ row, onUpdate, onRemove }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);

  // Debounced search über alle intern freigegebenen Easybill-Gruppen
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`/api/integrations/easybill/articles?q=${encodeURIComponent(query.trim())}`);
        if (r.ok) {
          const d = await r.json();
          setResults((d.items || []).slice(0, 15));
        }
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const pickArticle = (a) => {
    onUpdate({
      number: a.number || '',
      description: a.description || '',
      quantity: row.quantity || 1,
      unit: a.unit || 'Stück',
      single_price_net: a.single_price_net || 0,
      vat_percent: a.vat_percent || 19,
    });
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <div className="border border-gray-200 rounded-md p-2 bg-white" data-testid="extra-pos-row">
      <div className="relative mb-1.5">
        <input
          placeholder='Easybill-Artikel suchen (Nummer oder Bezeichnung, z. B. „Transport") — oder Freitext unten eintippen…'
          value={query}
          onChange={e => { setQuery(e.target.value); setShowResults(true); }}
          onFocus={() => setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 150)}
          className="w-full px-2 py-1 border border-purple-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-400"
          data-testid="extra-pos-search"
        />
        {showResults && query.trim().length >= 2 && (
          <div className="absolute z-30 left-0 right-0 mt-0.5 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
            {searching && <div className="px-2 py-1.5 text-[11px] text-gray-400">Suche …</div>}
            {!searching && results.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-gray-400">Keine Treffer in den intern freigegebenen Gruppen.</div>
            )}
            {results.map(a => (
              <button
                key={a.id}
                type="button"
                onMouseDown={() => pickArticle(a)}
                className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-purple-50 border-b border-gray-100 last:border-b-0"
                data-testid={`extra-pos-result-${a.id}`}
              >
                <div className="flex justify-between gap-2">
                  <span className="font-mono text-gray-400">{a.number || '–'}</span>
                  <span className="text-purple-700 tabular-nums">{fmtEur(a.single_price_net)}</span>
                </div>
                <div className="text-gray-700 truncate">{a.descriptionPreview || a.description}</div>
                {a.groupName && <div className="text-[10px] text-gray-400">{a.groupName}</div>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-12 gap-1.5">
        <input placeholder="Bezeichnung" value={row.description}
          onChange={ev => onUpdate({ ...row, description: ev.target.value })}
          className="col-span-5 px-2 py-1 border border-gray-200 rounded text-xs" />
        <input type="number" placeholder="Menge" value={row.quantity}
          onChange={ev => onUpdate({ ...row, quantity: Number(ev.target.value) })}
          className="col-span-2 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums" />
        <input type="number" step="0.01" placeholder="Preis netto" value={row.single_price_net}
          onChange={ev => onUpdate({ ...row, single_price_net: Number(ev.target.value) })}
          className="col-span-2 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums" />
        <input type="number" step="0.1" placeholder="MwSt %" value={row.vat_percent}
          onChange={ev => onUpdate({ ...row, vat_percent: Number(ev.target.value) })}
          className="col-span-2 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums" />
        <button
          type="button"
          onClick={onRemove}
          className="col-span-1 p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 flex items-center justify-center"
          title="Position entfernen"
          data-testid="btn-remove-extra-pos"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ConfirmedDeliveryDateField({ orderId, initial, onSaved, canEdit }) {
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Wenn das Modal neu lädt und die Order ein anderes confirmedDeliveryDate
  // hat, übernehmen wir es (z. B. nach Re-Import). Dirty-Flag schützt vor
  // dem Überschreiben einer aktiven User-Eingabe.
  useEffect(() => {
    if (!dirty) setValue(initial || '');
  }, [initial, dirty]);

  const save = async (next) => {
    setSaving(true);
    const r = await authFetch(`/api/orders/${orderId}/confirmed-delivery-date`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmedDeliveryDate: next || null }),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.success(next ? 'Liefertermin gesetzt' : 'Liefertermin entfernt');
      setDirty(false);
      onSaved?.(d.confirmedDeliveryDate || null);
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Speichern fehlgeschlagen');
    }
  };

  // Read-only Variante (Produktion / fehlende Berechtigung) — nur anzeigen,
  // wenn ein Wert gesetzt ist.
  if (!canEdit) {
    if (!value) return null;
    return (
      <div className="mt-2 text-xs text-gray-700 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="confirmed-delivery-date-readonly">
        <span className="text-[10px] font-semibold text-emerald-700 uppercase mr-1">Bestätigter Liefertermin (EES)</span>
        {new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-700 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="confirmed-delivery-date-field">
      <span className="text-[10px] font-semibold text-emerald-700 uppercase">Bestätigter Liefertermin (an Kunden)</span>
      <input
        type="date"
        value={value}
        onChange={(e) => { setValue(e.target.value); setDirty(true); }}
        className="h-7 px-2 rounded border border-emerald-300 bg-white text-xs"
        data-testid="confirmed-delivery-date-input"
      />
      <button
        onClick={() => save(value)}
        disabled={saving || !dirty}
        className="px-2.5 py-1 rounded bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735] disabled:opacity-50"
        data-testid="confirmed-delivery-date-save"
      >
        {saving ? 'Speichern…' : 'Speichern'}
      </button>
      {initial && (
        <button
          onClick={() => { setValue(''); setDirty(true); save(''); }}
          disabled={saving}
          className="px-2 py-1 rounded text-emerald-700 text-xs hover:bg-emerald-100"
          data-testid="confirmed-delivery-date-clear"
        >
          Entfernen
        </button>
      )}
      <span className="text-[10px] text-emerald-700/70">
        Wird im Kunden-Dashboard angezeigt. Leer lassen → ausgeblendet.
      </span>
    </div>
  );
}


// #3: Anfrage ↔ Auftrag Verknüpfung — zeigt die verknüpfte Anfrage (inkl.
// E-Mail) im Auftrag, schlägt die eindeutige Easybill-Nr.-Zuordnung vor und
// erlaubt die manuelle Verknüpfung per Suche.
function InquiryLinkPanel({ orderId, canEdit }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (query = '') => {
    setLoading(true);
    const url = `/api/integrations/easybill/orders/${orderId}/inquiry-link${query ? `?q=${encodeURIComponent(query)}` : ''}`;
    const r = await authFetch(url);
    if (r.ok) setState(await r.json());
    setLoading(false);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const linkTo = async (inquiryId) => {
    setBusy(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/inquiry-link`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inquiryId }),
    });
    setBusy(false);
    if (r.ok) { toast.success('Anfrage verknüpft'); setPicker(false); setQ(''); load(); }
    else toast.error('Verknüpfen fehlgeschlagen');
  };

  const unlink = async () => {
    setBusy(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/inquiry-link`, { method: 'DELETE' });
    setBusy(false);
    if (r.ok) { toast.success('Verknüpfung aufgehoben'); load(); }
    else toast.error('Aufheben fehlgeschlagen');
  };

  if (loading && !state) return <div className="text-xs text-gray-500">Lade Anfrage-Verknüpfung…</div>;

  const linked = state?.linked;
  const autoMatch = state?.autoMatch;
  const candidates = state?.candidates || [];

  if (linked) {
    return (
      <div className="text-xs text-gray-700" data-testid="order-inquiry-linked">
        <Mail className="w-3.5 h-3.5 inline mr-1 text-blue-600" />
        <strong>Anfrage</strong> — {linked.subject}
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          <span className="font-medium text-blue-700" data-testid="order-inquiry-email">
            {linked.customerEmail || 'keine E-Mail hinterlegt'}
          </span>
          <a href={`/dashboard/angebote?open=${linked.id}`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-blue-600 hover:underline" data-testid="order-inquiry-open">
            <ExternalLink className="w-3 h-3" /> Anfrage öffnen
          </a>
          {canEdit && (
            <button onClick={unlink} disabled={busy}
              className="inline-flex items-center gap-1 text-red-600 hover:underline disabled:opacity-50"
              data-testid="order-inquiry-unlink">
              <Trash2 className="w-3 h-3" /> Verknüpfung aufheben
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs text-gray-700" data-testid="order-inquiry-unlinked">
      <div className="flex items-center gap-2 flex-wrap">
        <Mail className="w-3.5 h-3.5 inline text-gray-400" />
        <span className="text-gray-500">Keine Anfrage verknüpft.</span>
        {canEdit && (
          <button onClick={() => setPicker(p => !p)}
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
            data-testid="order-inquiry-link-toggle">
            <LinkIcon className="w-3 h-3" /> Anfrage verknüpfen
          </button>
        )}
      </div>

      {autoMatch && canEdit && (
        <div className="mt-2 bg-green-50 border border-green-200 rounded p-2" data-testid="order-inquiry-automatch">
          <div className="text-[10px] font-semibold text-green-700 uppercase mb-1">Eindeutiger Vorschlag (Easybill-Nr.)</div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate">{autoMatch.subject}</div>
              <div className="text-gray-500 truncate">{autoMatch.customerEmail}</div>
            </div>
            <button onClick={() => linkTo(autoMatch.id)} disabled={busy}
              className="shrink-0 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              data-testid="order-inquiry-automatch-link">Verknüpfen</button>
          </div>
        </div>
      )}

      {picker && canEdit && (
        <div className="mt-2 border border-gray-200 rounded p-2 bg-white" data-testid="order-inquiry-picker">
          <div className="flex items-center gap-1 mb-2">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load(q)}
              placeholder="Anfrage suchen (Betreff, E-Mail, Name)…"
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-blue-400"
              data-testid="order-inquiry-search-input" />
            <button onClick={() => load(q)}
              className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
              data-testid="order-inquiry-search-btn">Suchen</button>
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
            {candidates.length === 0 && (
              <div className="text-gray-400 py-2 text-center">Keine Treffer</div>
            )}
            {candidates.map(c => (
              <div key={c.id} className="py-1.5 flex items-center justify-between gap-2"
                data-testid={`order-inquiry-candidate-${c.id}`}>
                <div className="min-w-0">
                  <div className="truncate">{c.subject}</div>
                  <div className="text-gray-500 truncate">
                    {c.customerEmail}
                    {c.matchReason === 'number' && ' · Nr.-Treffer'}
                    {c.matchReason === 'email' && ' · gleiche E-Mail'}
                  </div>
                </div>
                <button onClick={() => linkTo(c.id)} disabled={busy}
                  className="shrink-0 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  data-testid={`order-inquiry-candidate-link-${c.id}`}>Verknüpfen</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CommissioningPanel({ orderId }) {
  const [state, setState] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    const r = await authFetch(`/api/inventory/commission/${orderId}`);
    if (r.ok) setState(await r.json());
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, checked) => {
    setBusyKey(key);
    const r = await authFetch(`/api/inventory/commission/${orderId}/toggle`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, checked }),
    });
    setBusyKey(null);
    if (r.ok) { await load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Fehlgeschlagen'); }
  };

  if (!state) return null;
  const items = state.items || [];
  if (items.length === 0) return null;
  const done = items.filter(i => i.checked).length;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="commissioning-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[#32373c]">
          Artikel kommissionieren <span className="text-gray-500 font-normal">({done}/{items.length})</span>
        </h3>
        <span className="text-[10px] text-gray-500">Abhaken reserviert den verfügbaren Bestand</span>
      </div>
      {state.booked && (
        <div className="text-[11px] text-green-700 mb-2" data-testid="commissioning-booked">
          Lager bereits gebucht — Kommissionierung abgeschlossen.
        </div>
      )}
      <div className="divide-y divide-amber-100">
        {items.map(it => {
          const short = it.available < 0;
          return (
            <label key={it.key}
              className="flex items-center gap-2 py-1.5 text-xs cursor-pointer"
              data-testid={`commission-item-${it.key}`}>
              <input type="checkbox" checked={it.checked} disabled={state.booked || busyKey === it.key}
                onChange={e => toggle(it.key, e.target.checked)}
                className="w-4 h-4 accent-[#9ec043]"
                data-testid={`commission-check-${it.key}`} />
              <span className="flex-1 min-w-0">
                <span className="font-medium text-[#32373c]">{it.modell}</span>
                {it.hersteller && <span className="text-gray-400"> · {it.hersteller}</span>}
                <span className="text-gray-500"> · benötigt {it.required}</span>
              </span>
              <span className={`shrink-0 tabular-nums ${short ? 'text-red-600 font-semibold' : 'text-gray-500'}`}
                data-testid={`commission-avail-${it.key}`}>
                {short ? `fehlt ${Math.abs(it.available)}` : `verfügbar ${it.available}`}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({ data, setData, orderId, isProduktion, showPrices, onFinalize, finalizing, onSync, showSync, setShowSync, onReimport, onChecklistChanged }) {
  const o = data.order;
  const src = data.source;
  const bomDeducted = o.bomDeducted;
  const hasEasybill = !!o.easybillDocumentId;
  // 01.09.2026 (User-Wunsch): Bei Bestellungen heißt der Button nur
  // „Lager buchen" — es gibt kein Produktbegleitdokument. Gebucht werden
  // die Positionen aus dem Reiter „Lager" (Server ruft `bomDeduct` mit
  // `source='bestellung_inventory'` auf).
  const isBestellung = o.orderKind === 'bestellung';
  // Default-Zeile: leere Platzhalter — wird beim Öffnen der Sync-Section
  // durch einen echten Easybill-Artikel ersetzt (siehe useEffect unten).
  const [extras, setExtras] = useState([
    { number: '', description: 'Transport / Frachtkosten', quantity: 1, unit: 'pauschal', single_price_net: 0, vat_percent: 19 },
  ]);
  const [defaultLoaded, setDefaultLoaded] = useState(false);

  // Beim ersten Anzeigen der Sync-Section den Easybill-Artikel
  // „Transport inkl. Paletten-/Verpackungspauschale" suchen und als
  // erste Zeile setzen. Fallback bleibt die Freitext-Variante.
  useEffect(() => {
    if (!showSync || defaultLoaded) return;
    setDefaultLoaded(true);
    (async () => {
      try {
        const r = await authFetch('/api/integrations/easybill/articles?q=transport');
        if (!r.ok) return;
        const d = await r.json();
        const items = (d.items || []);
        // Bevorzuge Treffer mit „Palette" im Titel, sonst ersten Transport-Artikel
        const best = items.find(it => /palet/i.test(it.description || ''))
          || items.find(it => /transport/i.test(it.description || ''))
          || items[0];
        if (best) {
          setExtras(prev => {
            // nur ersetzen, wenn die erste Zeile noch der Default-Platzhalter ist
            const first = prev[0];
            if (first && !first.number && (first.single_price_net === 0)) {
              return [{
                number: best.number || '',
                description: best.description || best.descriptionPreview || 'Transport',
                quantity: 1,
                unit: best.unit || 'Stück',
                single_price_net: best.single_price_net || 0,
                vat_percent: best.vat_percent || 19,
              }, ...prev.slice(1)];
            }
            return prev;
          });
        }
      } catch { /* leise: Freitext-Default bleibt */ }
    })();
  }, [showSync, defaultLoaded]);

  const updExtra = (i, next) => setExtras(prev => prev.map((e, idx) => idx === i ? next : e));
  const removeExtra = (i) => setExtras(prev => prev.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4" data-testid="tab-overview-content">
      {/* Source */}
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-2">Quelle / Herkunft</div>
        {src.shopOrder ? (
          <div className="text-xs text-gray-700">
            <ShoppingBag className="w-3.5 h-3.5 inline mr-1 text-[#9ec043]" />
            <strong>Shop-Bestellung</strong> #{src.shopOrder.id.slice(0, 8)} · {src.shopOrder.customerEmail}
            {src.shopOrder.commission && <div className="mt-1">Kommission: {src.shopOrder.commission}</div>}
            {src.shopOrder.deliveryAddressMode === 'custom' && src.shopOrder.deliveryAddress && (
              <div className="mt-1 whitespace-pre-wrap">Lieferadresse: {src.shopOrder.deliveryAddress}</div>
            )}
            {/* Mitteilung vom Kunden — bevorzugt aus Auftrag (enriched
                mit Liefertermin-Hinweis, falls erkannt), Fallback auf
                Original-Text aus der Shop-Bestellung. */}
            {(data?.order?.customerNote || src.shopOrder.customerNote) && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-amber-900 whitespace-pre-wrap" data-testid="overview-customer-note">
                <div className="text-[10px] font-semibold text-amber-700 uppercase mb-1">Mitteilung vom Kunden</div>
                {data?.order?.customerNote || src.shopOrder.customerNote}
              </div>
            )}
            <ConfirmedDeliveryDateField
              orderId={orderId}
              initial={data?.order?.confirmedDeliveryDate || ''}
              onSaved={(v) => setData(d => d ? { ...d, order: { ...d.order, confirmedDeliveryDate: v } } : d)}
              canEdit={!isProduktion}
            />
          </div>
        ) : (
          <InquiryLinkPanel orderId={orderId} canEdit={!isProduktion} />
        )}
      </div>

      {/* Aufgaben & Checkliste (Etappe A/B) — für ALLE Aufträge, unabhängig
          von der Quelle. Zeigt Checkliste + Anhänge inline. Interne Notiz
          und Aufgaben-Liste liegen jetzt in eigenen Tabs (siehe „Interne
          Notiz" und „Aufgaben"). */}
      <div data-testid="overview-checklist-block">
        <OrderTasksChecklistPanel
          order={data.order}
          inline
          hideInternalNote
          hideAttachedTasks
          onChanged={onChecklistChanged}
        />
      </div>

      {/* Teilkommissionierung (Phase 3): Artikel aus dem PBD einzeln abhaken.
          Abhaken reserviert den verfügbaren Bestand; reale Buchung erst beim
          „Lager buchen" im Produktbegleitdokument. */}
      {!isProduktion && <CommissioningPanel orderId={orderId} />}

      {/* Easybill-Sync Status */}
      <div className={`rounded-lg border p-3 ${hasEasybill ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="text-xs flex-1">
            {hasEasybill ? (
              <>
                <strong className="text-blue-800">Easybill-Verknüpfung</strong>
                {' '}— Beleg #<strong>{o.easybillDocumentNumber || '(Entwurf, noch keine Nummer)'}</strong> ({({
                  CHARGE: isBestellung ? 'Lieferschein' : 'Auftrag',
                  CHARGE_CONFIRM: isBestellung ? 'Lieferschein' : 'Auftragsbestätigung',
                  DELIVERY: 'Lieferschein',
                  ORDER: 'Bestellung (Lieferant)',
                  INVOICE: 'Rechnung',
                  OFFER: 'Angebot',
                })[o.easybillDocumentType] || o.easybillDocumentType || (isBestellung ? 'Lieferschein' : 'Auftrag')})
                {o.easybillReimportedAt && <span className="text-gray-500"> · zuletzt re-importiert {fmtDate(o.easybillReimportedAt)}</span>}
                {!o.easybillDocumentNumber && (
                  <div className="mt-1.5 text-[11px] text-blue-700 bg-blue-100/60 rounded px-2 py-1.5 leading-relaxed">
                    <strong>Hinweis:</strong> Easybill vergibt die Auftragsnummer erst, sobald der Entwurf in Easybill freigegeben wurde
                    (Status nicht mehr „Entwurf"). Sobald freigegeben → hier „Aus Easybill aktualisieren" drücken, um die Nummer zu übernehmen.
                  </div>
                )}
              </>
            ) : (
              <>
                <strong className="text-purple-800">Noch nicht in Easybill</strong>
                {' '}— Auftrag in Easybill anlegen, um Auftragsbestätigung & Rechnung erzeugen zu können.
              </>
            )}
          </div>
        </div>
        {hasEasybill ? (
          !isProduktion && (
            <button onClick={onReimport}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
              data-testid="btn-reimport-from-easybill">
              <RefreshCw className="w-3.5 h-3.5" /> Aus Easybill aktualisieren
            </button>
          )
        ) : isProduktion ? (
          <div className="text-xs text-gray-400 italic mt-2" data-testid="produktion-sync-hidden">
            Easybill-Sync für die Rolle Produktion nicht verfügbar.
          </div>
        ) : !showSync ? (
          <button onClick={() => setShowSync(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700"
            data-testid="btn-sync-to-easybill">
            <ExternalLink className="w-3.5 h-3.5" /> In Easybill anlegen
          </button>
        ) : (
          <div className="space-y-2 mt-2">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
              Zusätzliche Positionen (Transport, Aufschläge, …) — Suche aus allen intern freigegebenen Easybill-Gruppen
            </div>
            {extras.map((e, i) => (
              <ExtraPositionRow
                key={i}
                row={e}
                onUpdate={(next) => updExtra(i, next)}
                onRemove={() => removeExtra(i)}
              />
            ))}
            <button onClick={() => setExtras(p => [...p, { number: '', description: '', quantity: 1, unit: 'Stück', single_price_net: 0, vat_percent: 19 }])}
              className="text-xs text-gray-500 hover:text-[#9ec043]" data-testid="btn-add-extra-pos">+ weitere Position</button>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setShowSync(false)} className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100">Abbrechen</button>
              <button onClick={() => onSync(extras.filter(e => e.description.trim() || e.single_price_net > 0))}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700"
                data-testid="btn-confirm-sync-to-easybill">
                <ExternalLink className="w-3.5 h-3.5" /> Jetzt in Easybill anlegen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Positionen — für Rollen ohne `orders.view_prices` ohne Preise
          (Matrix-gesteuert 25.08.2026, ersetzt hardcoded Produktion-Check) */}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-2">Positionen ({(o.lineItems || []).length})</div>
        <table className="w-full text-xs" data-testid={`overview-positions-${showPrices ? 'full' : 'masked'}`}>
          <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
            <tr>
              <th className="text-left px-2 py-1.5">Beschreibung</th>
              <th className="text-right px-2 py-1.5">Menge</th>
              {showPrices && <th className="text-right px-2 py-1.5">Einzel</th>}
              {showPrices && <th className="text-right px-2 py-1.5">Summe</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(o.lineItems || []).map((li, i) => (
              <tr key={i}>
                <td className="px-2 py-1.5 text-gray-700">
                  {li.number && <span className="text-gray-400 font-mono mr-1">{li.number}</span>}
                  {li.description}
                </td>
                <td className="text-right tabular-nums">{li.quantity} {li.unit}</td>
                {showPrices && <td className="text-right tabular-nums">{fmtEur(li.single_price_net)}</td>}
                {showPrices && <td className="text-right tabular-nums">{fmtEur((li.quantity || 0) * (li.single_price_net || 0))}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!showPrices && (
          <div className="mt-1 text-[10px] text-gray-400 italic">
            Preise für Ihre Rolle ausgeblendet (Action <code>orders.view_prices</code> fehlt).
          </div>
        )}
      </div>

      {o.internalNote && showPrices && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
          <strong>Interne Notiz:</strong> {o.internalNote}
        </div>
      )}

      {/* Lager / Finalisierung */}
      <div className={`rounded-lg border p-3 ${bomDeducted ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-start gap-2 mb-1">
          {bomDeducted ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />}
          <div className="text-xs flex-1">
            {bomDeducted ? (
              <>
                <strong className="text-emerald-700">Lager bereits gebucht</strong> · {fmtDate(o.bomDeductedAt)}
                {o.bomDeductSource && <span className="text-gray-500"> · Quelle: {o.bomDeductSource}</span>}
              </>
            ) : (
              <>
                <strong className="text-amber-800">Lager noch nicht gebucht.</strong>
                {' '}{isBestellung
                  ? 'Die bestellten Artikel werden aus dem Reiter „Lager" abgebucht.'
                  : 'Die Komponenten werden beim Finalisieren des Produktbegleitdokuments abgebucht.'}
              </>
            )}
          </div>
        </div>
        {/* 04.02.2026 (User-Wunsch): „Lager buchen"-Button hier nur noch
            bei Aufträgen — bei Bestellungen wird pro Position im Lager-Tab
            gebucht, ein globaler Buchen-Knopf ist irreführend. */}
        {!bomDeducted && !isBestellung && (
          <button onClick={onFinalize} disabled={finalizing}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735] disabled:opacity-50"
            data-testid="btn-finalize-bom">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {finalizing ? 'Buche …' : 'Produktbegleitdokument abschließen & Lager buchen'}
          </button>
        )}
      </div>
    </div>
  );
}

function MailsTab({ mails, onRemove }) {
  if (mails.length === 0) {
    return <div className="text-sm text-gray-400 text-center py-8">Keine E-Mails verknüpft.</div>;
  }
  return (
    <div className="space-y-2" data-testid="tab-mails-content">
      {mails.map(m => (
        <div key={m.id} className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`history-mail-${m.id}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-[#32373c] truncate">{m.subject || '(kein Betreff)'}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">
                {m.direction === 'out' ? 'Ausgehend an' : 'Eingegangen von'} {m.from} · {fmtDate(m.date)}
              </div>
            </div>
            <button onClick={() => onRemove(m.id)}
              className={`p-1 text-gray-400 hover:text-red-500 rounded ${m.source === 'inquiry' ? 'hidden' : ''}`}
              title="Verknüpfung entfernen">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {m.body
            ? <p className="text-[11px] text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid={`history-mail-body-${m.id}`}>{m.body}</p>
            : (m.snippet && <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap">{m.snippet}…</p>)}
        </div>
      ))}
    </div>
  );
}

function TasksTab({ orderId, order, initialTasks, onReload, users, currentUser }) {
  // Neue Aufgaben-Ansicht (25.08.2026): Aufgaben werden nur noch INNERHALB
  // des Auftrags angezeigt & bearbeitet (kein Redirect nach /aufgaben mehr).
  // Karten liegen untereinander (nicht als 4-Spalten-Kanban), sonst gleiche
  // Optik wie im globalen Aufgaben-Board (TaskCard/TaskDialog werden
  // wiederverwendet).
  const [tasks, setTasks] = useState(initialTasks || []);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`/api/tasks?sourceId=${encodeURIComponent(orderId)}&mine=false`);
    if (r.ok) setTasks(await r.json());
    setLoading(false);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const deleteTask = async (id) => {
    if (!window.confirm('Aufgabe wirklich löschen?')) return false;
    const r = await authFetch(`/api/tasks/${id}`, { method: 'DELETE' });
    if (r.ok) {
      toast.success('Aufgabe gelöscht');
      await load();
      onReload?.();
      return true;
    }
    const d = await r.json().catch(() => ({}));
    toast.error(d.detail || 'Löschen fehlgeschlagen');
    return false;
  };

  const defaultSource = {
    source: 'order',
    sourceId: orderId,
    sourceLabel: order?.easybillDocumentNumber
      || order?.customerCompany
      || order?.customerName
      || 'Auftrag',
  };

  return (
    <div className="space-y-3" data-testid="tab-tasks-content">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-[#32373c]">Aufgaben zu diesem Auftrag</div>
          <div className="text-[10px] text-gray-400">
            Diese Aufgaben liegen ausschließlich im Auftrag — sie erscheinen nicht mehr im globalen
            Aufgaben-Board.
          </div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735]"
          data-testid="btn-add-order-task"
        >
          <Plus className="w-3.5 h-3.5" /> Neue Aufgabe
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-gray-400 italic py-6 text-center">Lade Aufgaben …</div>
      ) : tasks.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-6">
          Noch keine Aufgaben zu diesem Auftrag.
        </div>
      ) : (
        <div className="space-y-2" data-testid="order-tasks-list">
          {tasks.map(t => (
            <TaskCard
              key={t.id}
              task={t}
              showStatus
              showFollowups
              onEdit={() => setEditing(t)}
              onDelete={() => deleteTask(t.id)}
            />
          ))}
        </div>
      )}

      {showNew && (
        <TaskDialog
          users={users}
          currentUser={currentUser}
          defaultSource={defaultSource}
          onClose={() => setShowNew(false)}
          onSaved={async () => { setShowNew(false); await load(); onReload?.(); }}
        />
      )}
      {editing && (
        <TaskDialog
          task={editing}
          users={users}
          currentUser={currentUser}
          onDelete={deleteTask}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); onReload?.(); }}
        />
      )}
    </div>
  );
}

function DocumentsTab({ orderId, onRemove }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`/api/orders/${orderId}/all-documents`);
    if (r.ok) {
      const d = await r.json();
      setItems(d.items || []);
    } else { setItems([]); }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const loadEasybillRelated = async () => {
    setLoadingRelated(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/related-documents`);
    setLoadingRelated(false);
    if (!r.ok) { toast.error('Easybill-Dokumente konnten nicht geladen werden'); return; }
    const d = await r.json();
    const extra = (d.documents || []).filter(rd => {
      // ignoriere das Hauptdokument selbst (steht schon in der Liste)
      if (!rd.easybillDocumentId) return false;
      return !items?.some(it => it.easybillDocumentId === rd.easybillDocumentId);
    }).map(rd => ({
      kind: 'easybill_document',
      easybillKind: rd.kind,
      label: rd.kind === 'INVOICE' ? 'Rechnung'
           : rd.kind === 'DELIVERY' ? 'Lieferschein'
           : rd.kind === 'CREDIT' ? 'Gutschrift' : (rd.kind || 'Easybill-Dokument'),
      title: rd.easybillDocumentNumber || 'Easybill-Dokument',
      subtitle: rd.title || '',
      easybillDocumentId: rd.easybillDocumentId,
      downloadUrl: `/api/integrations/easybill/documents/${rd.easybillDocumentId}/pdf?download=true`,
      viewUrl: `/api/integrations/easybill/documents/${rd.easybillDocumentId}/pdf`,
      createdAt: rd.documentDate,
      iconHint: 'easybill',
    }));
    if (extra.length === 0) {
      toast.info('Keine weiteren Easybill-Dokumente zu diesem Auftrag gefunden.');
    } else {
      toast.success(`${extra.length} weitere Easybill-Dokumente geladen`);
      setItems(prev => [...(prev || []), ...extra]);
    }
  };

  if (loading) return <div className="text-sm text-gray-400 text-center py-8">Lade Dokumente …</div>;
  if (!items || items.length === 0) {
    return (
      <div className="space-y-3" data-testid="tab-documents-content">
        <div className="text-sm text-gray-400 text-center py-8">Keine Dokumente zu diesem Auftrag.</div>
        <div className="text-center">
          <button onClick={loadEasybillRelated} disabled={loadingRelated}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 hover:border-[#9ec043] text-xs"
            data-testid="btn-load-easybill-related">
            {loadingRelated ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Easybill-Lieferscheine & Rechnungen abrufen
          </button>
        </div>
      </div>
    );
  }

  const iconFor = (it) => {
    if (it.kind === 'easybill_document') return <FileCheck2 className="w-4 h-4 text-blue-600" />;
    if (it.kind === 'product_document' || it.kind === 'product_document_combined')
      return <FileText className="w-4 h-4 text-[#9ec043]" />;
    return <FileText className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="space-y-2" data-testid="tab-documents-content">
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
          {items.length} Dokument{items.length === 1 ? '' : 'e'}
        </span>
        <button onClick={loadEasybillRelated} disabled={loadingRelated}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-white border border-gray-200 hover:border-[#9ec043] text-gray-600"
          data-testid="btn-load-easybill-related">
          {loadingRelated ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Easybill aktualisieren
        </button>
      </div>
      {items.map((it, i) => (
        <div key={`${it.kind}-${it.docId || it.easybillDocumentId || i}`}
             className="bg-white border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3"
             data-testid={`history-doc-${it.kind}-${i}`}>
          <div className="min-w-0 flex-1 flex items-start gap-2">
            {iconFor(it)}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{it.label}</div>
              <div className="text-xs font-medium text-[#32373c] truncate">{it.title}</div>
              {it.subtitle && <div className="text-[10px] text-gray-500 truncate">{it.subtitle}</div>}
              <div className="text-[10px] text-gray-400 mt-0.5">{fmtDate(it.createdAt)}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a href={`${process.env.REACT_APP_BACKEND_URL}${it.viewUrl}`}
               target="_blank" rel="noreferrer"
               onClick={e => { e.preventDefault();
                 // Auth über fetch + Blob, weil das href keinen Auth-Header trägt
                 (async () => {
                   const r = await authFetch(it.viewUrl);
                   if (!r.ok) { toast.error('PDF konnte nicht geöffnet werden'); return; }
                   const blob = await r.blob();
                   const url = URL.createObjectURL(blob);
                   window.open(url, '_blank');
                   setTimeout(() => URL.revokeObjectURL(url), 4000);
                 })();
               }}
               className="p-1.5 text-gray-500 hover:text-[#9ec043] rounded hover:bg-gray-50"
               title="Anzeigen">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={async () => {
                const r = await authFetch(it.downloadUrl);
                if (!r.ok) { toast.error('Download fehlgeschlagen'); return; }
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                const ext = (it.kind === 'knowledge_document' && (it.subtitle || '').match(/\.[a-z0-9]+$/i))
                  ? '' : '.pdf';
                a.href = url; a.download = `${(it.label || 'dokument').replace(/[^\w-]/g, '_')}_${it.title || ''}${ext}`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1500);
              }}
              className="p-1.5 text-gray-500 hover:text-[#9ec043] rounded hover:bg-gray-50"
              title="Herunterladen"
              data-testid={`btn-download-${it.kind}-${i}`}>
              <Download className="w-3.5 h-3.5" />
            </button>
            {it.kind === 'knowledge_document' && onRemove && (
              <button onClick={() => onRemove(it.docId)}
                className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                title="Verknüpfung lösen">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductDocsTab({ docs, orderId, role, onRemove, onChanged }) {
  const [editingDocId, setEditingDocId] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const canRegenerate = ['admin', 'innendienst', 'technik', 'support'].includes(role);

  const regenerate = async () => {
    if (!canRegenerate) return;
    const ok = window.confirm(
      'Alle bestehenden Begleitdokumente dieses Auftrags werden gelöscht und ' +
      'auf Basis der aktuellen Auftragspositionen + Systemdatenbank neu erkannt.\n\n' +
      'Bereits eingetragene Seriennummern blockieren diesen Vorgang. Fortfahren?'
    );
    if (!ok) return;
    setRegenerating(true);
    const r = await authFetch(`/api/order-documents/by-order/${orderId}/regenerate`, { method: 'POST' });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.deleted} alte gelöscht, ${d.created} neu erkannt`);
      onChanged?.();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Regenerieren fehlgeschlagen');
    }
    setRegenerating(false);
  };

  if (docs.length === 0 && !canRegenerate) {
    return <div className="text-sm text-gray-400 text-center py-8">Noch kein Produktbegleitdokument erzeugt.</div>;
  }

  return (
    <div className="space-y-2" data-testid="tab-productDocs-content">
      {canRegenerate && (
        <div className="flex items-center justify-end gap-2 mb-1">
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-[#f4f7ec] hover:text-[#7da235] hover:border-[#9ec043] disabled:opacity-50"
            title="Alle Begleitdokumente löschen und neu aus den Auftragspositionen + Systemdatenbank erkennen"
            data-testid="btn-regenerate-product-docs"
          >
            <Sparkles className="w-3 h-3" />
            {regenerating ? 'Erkenne neu …' : 'Neu erkennen'}
          </button>
        </div>
      )}
      {docs.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-8">Noch kein Produktbegleitdokument erzeugt.</div>
      ) : (
        docs.map(d => (
          <div
            key={d.id}
            className="bg-white border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-2 hover:border-[#9ec043] hover:shadow-sm cursor-pointer transition"
            data-testid={`history-pb-${d.id}`}
            onClick={() => setEditingDocId(d.id)}
          >
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-[#32373c]">{d.systemName || '(ohne System)'} × {d.quantity}</div>
              <div className="text-[10px] text-gray-400">
                Kommission: {d.kommission || '–'} · {d.componentsCount} Komponenten · {fmtDate(d.updatedAt || d.createdAt)}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setEditingDocId(d.id); }}
              className="p-1 text-gray-400 hover:text-[#7da235] rounded"
              title="Begleitdokument bearbeiten"
              data-testid={`btn-edit-pb-${d.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(d.id); }}
              className="p-1 text-gray-400 hover:text-red-500 rounded"
              title="Produktbegleitdokument löschen"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))
      )}
      {editingDocId && (
        <OrderDocumentEditor
          orderId={orderId}
          docId={editingDocId}
          onClose={() => { setEditingDocId(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function BestellungInventoryTab({ order, onBooked }) {
  // Punkt 3 Runde 3 (28.02.2026): Pro Bestell-Position eine Zeile mit
  // Seriennummer-Eingabe (+ Kamera-Trick via `capture="environment"`)
  // und lokalem Buchungs-Button. Ruft `POST /api/shop/orders/{id}/book-inventory`.
  const [rows, setRows] = useState(() =>
    (order.lineItems || []).map((li) => ({
      articleNumber: li.number || '',
      description:   li.description || li.title || '',
      quantity:      li.quantity || 1,
      serialInput:   '',
      serialNumbers: li.serialNumbers || [],
      bookedAt:      li.inventoryBookedAt || null,
    })),
  );
  const [busy, setBusy] = useState(false);
  // 04.02.2026 (User-Wunsch): Preview welche Zeile aktuell auf ein
  // Lager-Item matchable ist — grüner/gelber Punkt vor der Beschreibung.
  const [previewByIndex, setPreviewByIndex] = useState({});
  // 18.06.26 (User-Wunsch): „Zuordnen" direkt aus einer roten Position —
  // legt die EasyBill-Nr./-Bezeichnung als Alias beim gewählten Lager-Artikel an.
  const { can } = usePermissions();
  const [assignIdx, setAssignIdx] = useState(null);
  const [invQuery, setInvQuery] = useState('');
  const [invResults, setInvResults] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const loadPreview = useCallback(async () => {
    const r = await authFetch(`/api/shop/orders/${order.id}/book-inventory/preview`);
    if (!r.ok) return;
    const d = await r.json().catch(() => null);
    const map = {};
    (d?.lines || []).forEach(l => { map[l.lineIndex] = l; });
    setPreviewByIndex(map);
  }, [order.id]);

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await loadPreview(); })();
    return () => { alive = false; };
  }, [loadPreview]);

  // Lager-Artikel suchen (für den Zuordnen-Picker)
  useEffect(() => {
    if (assignIdx === null) return;
    let alive = true;
    setInvLoading(true);
    const t = setTimeout(async () => {
      const q = invQuery.trim();
      const r = await authFetch(`/api/inventory${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      if (!alive) return;
      const d = await r.json().catch(() => null);
      setInvResults((d?.items || []).slice(0, 40));
      setInvLoading(false);
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [invQuery, assignIdx]);

  const openAssign = (i) => {
    setAssignIdx(i);
    // Vorbelegung der Suche mit dem Hersteller-/Modell-Hinweis aus der Beschreibung
    setInvQuery('');
    setInvResults([]);
  };

  const assignToItem = async (item) => {
    if (assignIdx === null) return;
    const r = rows[assignIdx];
    // EasyBill-Artikelnummer (falls vorhanden, nicht „—") UND Beschreibung
    // als Aliase hinterlegen → künftige Bestellungen matchen automatisch.
    const aliases = [];
    const num = (r.articleNumber || '').trim();
    if (num && num !== '—') aliases.push(num);
    if ((r.description || '').trim()) aliases.push(r.description.trim());
    if (aliases.length === 0) { toast.error('Keine Bezeichnung/Nummer zum Zuordnen'); return; }
    setAssigning(true);
    const resp = await authFetch(`/api/inventory/${item.id}/aliases`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ aliases }),
    });
    setAssigning(false);
    if (resp.ok) {
      toast.success(`Zugeordnet: ${r.description || num} → ${item.hersteller || ''} ${item.modell}`.trim());
      setAssignIdx(null); setInvQuery(''); setInvResults([]);
      await loadPreview();
    } else {
      const d = await resp.json().catch(() => ({}));
      toast.error(d.detail || 'Zuordnung fehlgeschlagen');
    }
  };

  const addSerial = (i, val) => {
    const s = (val || '').trim();
    if (!s) return;
    setRows(cur => cur.map((r, k) => k === i
      ? { ...r, serialNumbers: [...r.serialNumbers, s], serialInput: '' }
      : r));
  };
  const removeSerial = (i, k) => {
    setRows(cur => cur.map((r, kk) => kk === i
      ? { ...r, serialNumbers: r.serialNumbers.filter((_, j) => j !== k) }
      : r));
  };

  const bookLine = async (i) => {
    setBusy(true);
    const r = rows[i];
    // 04.02.2026 (User-Bug): Wenn Seriennummer noch im Input-Feld steht
    // (User hat kein Enter gedrückt und direkt auf „Buchen" geklickt),
    // muss sie automatisch mit übernommen werden — sonst geht sie verloren.
    const pending = (r.serialInput || '').trim();
    const finalSerials = pending
      ? [...r.serialNumbers, pending]
      : r.serialNumbers;
    if (pending) {
      setRows(cur => cur.map((x, k) => k === i
        ? { ...x, serialNumbers: finalSerials, serialInput: '' } : x));
    }
    const payload = { lines: [{
      lineIndex: i,
      quantity: Number(r.quantity) || 0,
      articleNumber: r.articleNumber,
      serialNumbers: finalSerials,
    }]};
    const resp = await authFetch(`/api/shop/orders/${order.id}/book-inventory`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setBusy(false);
    if (resp.ok) {
      const d = await resp.json();
      if ((d.warnings || []).length > 0) toast.warning(d.warnings.join(' · '));
      if ((d.booked || []).length > 0) {
        toast.success(`Lager gebucht: ${r.articleNumber || r.description}`);
        // 04.02.2026: lokal sofort als „gebucht" markieren, damit die
        // Seriennummern in der UI erhalten bleiben — der Parent-Refresh
        // remounted die Component u. U. nicht, dann bliebe `bookedAt`
        // sonst hängen.
        setRows(cur => cur.map((x, k) => k === i
          ? { ...x, bookedAt: new Date().toISOString(), serialNumbers: finalSerials, serialInput: '' }
          : x));
      }
      await onBooked?.();
    } else {
      const d = await resp.json().catch(() => ({}));
      toast.error(d.detail || 'Buchung fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-3" data-testid="tab-inventory-content-bestellung">
      <div className="text-[10px] text-gray-500">
        Trage pro Position die Seriennummern ein (per Eingabe oder Kamera-Scan)
        und buche das Lager individuell ab.
      </div>
      {rows.length === 0 && (
        <div className="text-sm text-gray-400 text-center py-8">Keine Positionen.</div>
      )}
      {rows.map((r, i) => (
        <div key={i} className={`rounded-xl border p-3 ${r.bookedAt ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-gray-200'}`}
             data-testid={`bestellung-line-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-[#32373c] truncate flex items-center gap-1.5">
                {/* 04.02.2026: Zuordnungs-Status-Icon. Grün=matchable,
                    Gelb=kein Match (Buchen wird fehlschlagen),
                    Grau=noch nicht geprüft. */}
                {(() => {
                  const p = previewByIndex[i];
                  if (r.bookedAt || p?.alreadyBooked) return null;
                  if (!p) return (
                    <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" title="Prüfe Zuordnung..." data-testid={`match-pending-${i}`} />
                  );
                  if (p.matched && p.sufficient) return (
                    <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"
                          title={`Lager: ${p.itemLabel} — Bestand: ${p.stock}`}
                          data-testid={`match-ok-${i}`} />
                  );
                  if (p.matched && !p.sufficient) return (
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0"
                          title={`Lager: ${p.itemLabel} — nur ${p.stock} von ${r.quantity} vorhanden`}
                          data-testid={`match-lowstock-${i}`} />
                  );
                  return (
                    <span className="w-2 h-2 rounded-full bg-red-500 shrink-0"
                          title="Kein Lager-Artikel zugeordnet — Buchen schlägt fehl"
                          data-testid={`match-none-${i}`} />
                  );
                })()}
                <span className="truncate">{r.articleNumber || '—'} &middot; {r.description}</span>
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                <span>Menge: {r.quantity}</span>
                {previewByIndex[i]?.matched && !r.bookedAt && (
                  <span className="text-gray-400">
                    → {previewByIndex[i].itemLabel} (Bestand: {previewByIndex[i].stock})
                  </span>
                )}
              </div>
            </div>
            {r.bookedAt ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                &#10003; Gebucht
              </span>
            ) : (
              <div className="flex items-center gap-1.5 shrink-0">
                {(() => {
                  const p = previewByIndex[i];
                  const unmatched = p && !p.matched && !p.alreadyBooked;
                  if (!unmatched || !can('inventory.update')) return null;
                  return (
                    <button
                      onClick={() => (assignIdx === i ? setAssignIdx(null) : openAssign(i))}
                      className="text-[11px] px-3 py-1.5 rounded-lg border border-[#9ec043] text-[#6d8a2e] hover:bg-[#f3f9e4] inline-flex items-center gap-1"
                      data-testid={`btn-assign-line-${i}`}
                      title="Diese EasyBill-Position einem Lager-Artikel zuordnen"
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                      Zuordnen
                    </button>
                  );
                })()}
                <button
                  onClick={() => bookLine(i)} disabled={busy}
                  className="text-[11px] px-3 py-1.5 rounded-lg bg-[#9ec043] text-white hover:bg-[#7ca735] disabled:opacity-50"
                  data-testid={`btn-book-line-${i}`}
                >Lager buchen</button>
              </div>
            )}
          </div>
          {/* 04.02.2026: Seriennummern IMMER anzeigen, auch nach Buchung — nur so
              ist nachvollziehbar welche Seriennummern zu welcher Position gebucht wurden. */}
          {r.bookedAt && r.serialNumbers?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid={`serial-list-booked-${i}`}>
              <span className="text-[10px] text-gray-500 mr-1">Seriennummern:</span>
              {r.serialNumbers.map((sn, k) => (
                <span key={k} className="text-[10px] px-2 py-0.5 rounded bg-white border border-emerald-200 text-emerald-800 font-mono">
                  {sn}
                </span>
              ))}
            </div>
          )}
          {!r.bookedAt && (
            <div className="mt-2 flex items-center gap-2">
              <input type="text" value={r.serialInput}
                placeholder="Seriennummer eintragen&hellip;"
                onChange={e => setRows(cur => cur.map((x, k) => k === i ? { ...x, serialInput: e.target.value } : x))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSerial(i, r.serialInput); } }}
                className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs"
                data-testid={`serial-input-${i}`} />
              <label className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 text-[11px] cursor-pointer hover:bg-gray-50"
                     data-testid={`camera-serial-${i}`}>
                <Camera className="w-3.5 h-3.5" />
                Foto
                <input type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={e => { if (e.target.files?.[0]) toast.info('Foto aufgenommen &mdash; Seriennr. bitte manuell eintragen.'); }} />
              </label>
              <button onClick={() => addSerial(i, r.serialInput)}
                      className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-[11px]"
                      data-testid={`btn-add-serial-${i}`}>+ Hinzu</button>
            </div>
          )}
          {r.serialNumbers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {r.serialNumbers.map((s, k) => (
                <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[10px]">
                  {s}
                  {!r.bookedAt && (
                    <button onClick={() => removeSerial(i, k)} className="text-red-500 hover:text-red-700">&times;</button>
                  )}
                </span>
              ))}
            </div>
          )}
          {/* 18.06.26: Zuordnen-Picker — Lager-Artikel suchen und die
              EasyBill-Nr./-Bezeichnung als Alias hinterlegen. */}
          {assignIdx === i && (
            <div className="mt-3 rounded-lg border border-[#9ec043]/50 bg-[#f8fbef] p-3" data-testid={`assign-panel-${i}`}>
              <div className="text-[11px] text-gray-600 mb-2">
                Ordne <span className="font-medium">{r.articleNumber && r.articleNumber !== '—' ? `${r.articleNumber} · ` : ''}{r.description}</span> einem Lager-Artikel zu.
                Die EasyBill-Bezeichnung/-Nummer wird als Alias gespeichert.
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <input
                  autoFocus type="text" value={invQuery}
                  onChange={e => setInvQuery(e.target.value)}
                  placeholder="Lager-Artikel suchen (Hersteller/Modell)&hellip;"
                  className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs"
                  data-testid={`assign-search-${i}`}
                />
                <button onClick={() => setAssignIdx(null)} className="text-[11px] text-gray-400 hover:text-gray-700 px-1">Abbrechen</button>
              </div>
              <div className="max-h-56 overflow-auto divide-y divide-gray-100 rounded border border-gray-100 bg-white">
                {invLoading && <div className="text-[11px] text-gray-400 px-2 py-3 text-center">Suche&hellip;</div>}
                {!invLoading && invResults.length === 0 && (
                  <div className="text-[11px] text-gray-400 px-2 py-3 text-center">Keine Treffer</div>
                )}
                {!invLoading && invResults.map(item => (
                  <button
                    key={item.id} disabled={assigning}
                    onClick={() => assignToItem(item)}
                    className="w-full text-left px-2 py-1.5 hover:bg-[#f3f9e4] disabled:opacity-50 flex items-center justify-between gap-2"
                    data-testid={`assign-result-${item.id}`}
                  >
                    <span className="min-w-0 truncate text-xs text-[#32373c]">
                      <span className="text-gray-400">{item.hersteller || '—'}</span> · {item.modell}
                    </span>
                    <span className="text-[10px] text-gray-400 shrink-0">Bestand: {item.stock ?? 0}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


function InventoryTab({ tx }) {
  if (tx.length === 0) return <div className="text-sm text-gray-400 text-center py-8">Keine Lager-Buchungen zu diesem Auftrag.</div>;
  return (
    <div className="space-y-1" data-testid="tab-inventory-content">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
          <tr>
            <th className="text-left px-2 py-1.5">Datum</th>
            <th className="text-left px-2 py-1.5">Typ</th>
            <th className="text-left px-2 py-1.5">Begründung</th>
            <th className="text-right px-2 py-1.5">Delta</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tx.map(t => (
            <tr key={t.id}>
              <td className="px-2 py-1.5 text-gray-500">{fmtDate(t.createdAt)}</td>
              <td className="px-2 py-1.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{t.type}</span>
              </td>
              <td className="px-2 py-1.5 text-gray-700">{t.reason}</td>
              <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${t.delta < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {t.delta > 0 ? '+' : ''}{t.delta}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InternalNoteTab({ orderId, order, role }) {
  // Interne Notiz als eigener Tab (User-Anforderung 25.08.2026). Sichtbar
  // nur für Admin/Innendienst/Team; andere Rollen erhalten einen Hinweis.
  const canSee = ['admin', 'innendienst', 'team'].includes((role || '').toLowerCase());
  const [note, setNote] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!canSee) { setLoading(false); return; }
    setLoading(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/internal-note`);
    if (r.ok) {
      const d = await r.json();
      setNote(d.note || '');
      setMeta({ updatedAt: d.updatedAt, updatedBy: d.updatedBy });
    }
    setLoading(false);
  }, [orderId, canSee]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/internal-note`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json();
      setMeta({ updatedAt: d.updatedAt, updatedBy: d.updatedBy });
      toast.success('Interne Notiz gespeichert');
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Speichern fehlgeschlagen');
    }
  };

  if (!canSee) {
    return (
      <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-3"
           data-testid="internal-note-forbidden">
        Diese Notiz ist nur für Admin, Innendienst und Team-Rollen sichtbar.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="tab-note-content">
      <div>
        <div className="text-sm font-semibold text-[#32373c]">Interne Notiz</div>
        <div className="text-[10px] text-gray-400 mt-0.5">
          Für {order?.customerCompany || order?.customerName || 'Auftrag'} — nicht sichtbar für Kunden,
          Vertrieb, Technik oder Produktion. Ideal für Absprachen mit Kunden, nachträgliche Infos aus
          Telefonaten oder interne To-Do-Merker.
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-gray-400 italic py-6 text-center">Lade Notiz …</div>
      ) : (
        <>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={14}
            placeholder="Interne Notiz zum Auftrag …"
            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#9ec043]"
            data-testid="internal-note-tab-textarea"
          />
          <div className="flex items-center justify-between">
            <div className="text-[10px] text-gray-400">
              {meta?.updatedAt && (
                <>Zuletzt geändert: {new Date(meta.updatedAt).toLocaleString('de-DE')}
                  {meta.updatedBy && ` · ${meta.updatedBy}`}</>
              )}
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
              data-testid="btn-save-internal-note-tab"
            >
              {saving ? 'Speichere …' : 'Notiz speichern'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}



function ComponentsTab({ orderId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`/api/order-documents/by-order/${orderId}/components`);
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, [orderId]);

  if (loading) return <div className="p-4 text-sm text-gray-400">Lade Komponenten …</div>;
  const docs = data?.documents || [];
  if (docs.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-400" data-testid="components-empty">
        Noch kein Produktbegleitdokument für diesen Auftrag erstellt — Komponenten und Seriennummern
        erscheinen hier, sobald ein Begleitdokument finalisiert wurde.
      </div>
    );
  }
  const fn = (s) => (s || '').toLowerCase();
  const fq = filter.trim().toLowerCase();
  return (
    <div className="space-y-3" data-testid="components-tab">
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
          Verbaute Komponenten {data?.count > 1 && `(${data.count} Dokumente)`}
        </span>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter: Serien-Nr., Modell, Komponente …"
          className="h-7 text-xs px-2 border border-gray-200 rounded w-56"
          data-testid="components-filter-input"
        />
      </div>
      {docs.map(doc => {
        const filteredComps = doc.components.filter(c => !fq
          || fn(c.serial).includes(fq) || fn(c.model).includes(fq)
          || fn(c.label).includes(fq) || fn(c.notes).includes(fq)
          || fn(c.section).includes(fq));
        const headerHits = doc.header.filter(h => !fq || fn(h.value).includes(fq) || fn(h.label).includes(fq));
        if (fq && filteredComps.length === 0 && headerHits.length === 0) return null;
        return (
          <div key={doc.docId} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`components-doc-${doc.docId}`}>
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-[#32373c]">
                {doc.systemName || 'System'} {doc.kommission && <span className="text-gray-500 font-normal">· {doc.kommission}</span>}
              </div>
              <span className="text-[10px] text-gray-400">{fmtDate(doc.createdAt)}</span>
            </div>
            {headerHits.length > 0 && (
              <div className="px-3 py-2 bg-amber-50/50 border-b border-amber-100 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {headerHits.map((h, i) => (
                  <div key={i}>
                    <span className="text-gray-500">{h.label}:</span> <strong className="font-mono">{h.value}</strong>
                  </div>
                ))}
              </div>
            )}
            {filteredComps.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400 italic">— keine Komponenten in diesem Dokument —</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-white border-b border-gray-100 text-[10px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">Sektion</th>
                    <th className="text-left px-3 py-1.5 font-medium">Bezeichnung</th>
                    <th className="text-left px-3 py-1.5 font-medium">Modell/Variante</th>
                    <th className="text-left px-3 py-1.5 font-medium">Seriennummer</th>
                    <th className="text-right px-3 py-1.5 font-medium">Anz.</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredComps.map((c, i) => (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-500">{c.section}</td>
                      <td className="px-3 py-1.5">{c.label}</td>
                      <td className="px-3 py-1.5 text-gray-700">{c.model || '–'}</td>
                      <td className="px-3 py-1.5">
                        {c.serial ? <span className="font-mono text-[11px] bg-[#9ec043]/10 text-[#7ca735] px-1.5 py-0.5 rounded">{c.serial}</span> : <span className="text-gray-300">–</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}



// 01.09.2026 (User-Wunsch): Lightweight-Komponenten-Reiter für Bestellungen.
// Zeigt die bestellten Line-Items (Anzahl · Nr · Bezeichnung) und, falls im
// Reiter „Lager" bereits gescannt, die Seriennummern. Camera-Scan + Manuelles
// Nachtragen findet weiterhin im Reiter „Lager" statt (BestellungInventoryTab).
function BestellungComponentsTab({ order }) {
  const lines = order.lineItems || [];
  if (lines.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-400" data-testid="bestellung-components-empty">
        Keine bestellten Positionen gefunden.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="bestellung-components-tab">
      <div className="text-[11px] text-gray-500 leading-relaxed">
        Automatisch befüllt aus den bestellten Artikeln. Seriennummern und
        Lagerbuchung werden im Reiter <strong>Lager</strong> per Kamera-Scan
        oder manuell eingetragen.
      </div>
      <table className="w-full text-xs bg-white border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-right px-3 py-1.5 font-medium w-16">Anz.</th>
            <th className="text-left px-3 py-1.5 font-medium w-28">Art.-Nr.</th>
            <th className="text-left px-3 py-1.5 font-medium">Bezeichnung</th>
            <th className="text-left px-3 py-1.5 font-medium">Seriennummern</th>
            <th className="text-right px-3 py-1.5 font-medium w-24">Status</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((li, i) => (
            <tr key={i} className="border-t border-gray-100" data-testid={`bestellung-comp-line-${i}`}>
              <td className="px-3 py-1.5 text-right tabular-nums">{li.quantity ?? '—'}</td>
              <td className="px-3 py-1.5 font-mono text-[11px]">{li.number || '—'}</td>
              <td className="px-3 py-1.5">{li.description || li.name || '—'}</td>
              <td className="px-3 py-1.5">
                {(li.serialNumbers && li.serialNumbers.length > 0)
                  ? li.serialNumbers.map((sn, j) => (
                    <span key={j} className="inline-block font-mono text-[10px] bg-[#9ec043]/10 text-[#7ca735] px-1.5 py-0.5 rounded mr-1 mb-1">
                      {sn}
                    </span>
                  ))
                  : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-3 py-1.5 text-right">
                {li.inventoryBookedAt
                  ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Gebucht</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Offen</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
